import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/contexts/CartContext";
import LandingPage from "@/pages/LandingPage";
import MobileHomePage from "@/pages/MobileHomePage";
import MobileRoleHomePage from "@/pages/MobileRoleHomePage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import RegisterPage from "@/pages/RegisterPage";
import EmailVerificationPage from "@/pages/EmailVerificationPage";
import LegalPage from "@/pages/LegalPage";
import AccountDeletionPage from "@/pages/AccountDeletionPage";
import DashboardLayout from "@/layouts/DashboardLayout";
import PartnerDashboard from "@/pages/PartnerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import BuyerDashboard from "@/pages/BuyerDashboard";
import CataloguePage from "@/pages/CataloguePage";
import ProductSubmitPage from "@/pages/ProductSubmitPage";
import OrdersPage from "@/pages/OrdersPage";
import TeamPage from "@/pages/TeamPage";
import BonusesPage from "@/pages/BonusesPage";
import ProductsManagementPage from "@/pages/ProductsManagementPage";
import CartPage from "@/pages/CartPage";
import ActivationManagementPage from "@/pages/ActivationManagementPage";
import AnalyticsDashboardPage from "@/pages/AnalyticsDashboardPage";
import PublicCataloguePage from "@/pages/PublicCataloguePage";
import VerificationPage from "@/pages/VerificationPage";
import AdminKycPage from "@/pages/AdminKycPage";
import FounderDashboard from "@/pages/FounderDashboard";
import ArticoCashPage from "@/pages/ArticoCashPage";
import UserManagementPage from "@/pages/UserManagementPage";
import AdminUsersDirectoryPage from "@/pages/AdminUsersDirectoryPage";
import AdminPartnerLineagePage from "@/pages/AdminPartnerLineagePage";
import ProfilePage from "@/pages/ProfilePage";
import MesDocumentsPage from "@/pages/MesDocumentsPage";
import ArticoLibraryPage from "@/pages/ArticoLibraryPage";
import MessagesPage from "@/pages/MessagesPage";
import SystemOpsPage from "@/pages/SystemOpsPage";
import WithdrawalManagementPage from "@/pages/WithdrawalManagementPage";
import PlatformSettingsPage from "@/pages/PlatformSettingsPage";
import ModerationQueuePage from "@/pages/ModerationQueuePage";
import { authAPI } from "@/services/api";
import { startBackendWakeLoop, stopBackendWakeLoop } from "@/services/backendWake";
import { ADMIN_RANKS, canAccessCashPages, hasAdminRank, hasExplicitAdminRank, hasFullAdminRank, isFounderAccount } from "@/utils/adminRanks";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isNativeApp = Capacitor.isNativePlatform();

  useEffect(() => {
    startBackendWakeLoop();

    const token = localStorage.getItem("mmj_token");
    const userData = localStorage.getItem("mmj_user");
    
    if (token && userData) {
      const cachedUser = JSON.parse(userData);
      setUser(cachedUser);
      authAPI.getMe()
        .then((res) => {
          localStorage.setItem("mmj_user", JSON.stringify(res.data));
          setUser(res.data);
        })
        .catch(() => {});
    }
    setLoading(false);

    return () => stopBackendWakeLoop();
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem("mmj_token", token);
    localStorage.setItem("mmj_user", JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("mmj_token");
    localStorage.removeItem("mmj_user");
    setUser(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;
  }

  return (
    <div className="App">
      <CartProvider>
        <BrowserRouter>
          <AndroidBackButtonBridge user={user} />
          <Routes>
            <Route
              path="/"
              element={
                isNativeApp ? (
                  <MobileSessionEntry user={user} />
                ) : (
                  <LandingPage />
                )
              }
            />
            <Route path="/public/catalogue" element={<PublicCataloguePage />} />
            <Route path="/mobile/admin" element={<MobileRoleHomePage user={user} />} />
            <Route path="/mobile/partner" element={<MobileRoleHomePage user={user} />} />
            <Route path="/mobile/buyer" element={<MobileRoleHomePage user={user} />} />
            <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ForgotPasswordPage />} />
            <Route path="/register" element={<RegisterPage onLogin={handleLogin} />} />
            <Route path="/verify-email" element={<EmailVerificationPage user={user} onVerified={setUser} />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/delete-account" element={<AccountDeletionPage />} />
            
            <Route
              path="/dashboard"
              element={
                user && needsEmailVerification(user) ? (
                  <Navigate to="/verify-email" />
                ) : user ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    {user.role === "admin" && <AdminDashboard user={user} />}
                    {user.role === "partner" && <PartnerDashboard user={user} />}
                    {user.role === "buyer" && <BuyerDashboard user={user} />}
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/profile"
              element={
                user ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <ProfilePage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/documents"
              element={
                user ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <MesDocumentsPage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            
            <Route
              path="/catalogue"
              element={
                user ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <CataloguePage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/public/catalogue" replace />
                )
              }
            />

            <Route
              path="/cart"
              element={
                user ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <CartPage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/library"
              element={
                user && ["partner", "buyer", "admin"].includes(user.role) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <ArticoLibraryPage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            
            <Route
              path="/products/submit"
              element={
                user && user.role === "partner" ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <ProductSubmitPage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/admin/catalogue-libre/submit"
              element={
                user && hasExplicitAdminRank(user, ADMIN_RANKS.CL) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <ProductSubmitPage institutional />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            
            <Route
              path="/orders"
              element={
                user && (user.role !== "admin" || hasAdminRank(user, ADMIN_RANKS.C, ADMIN_RANKS.CAISSE)) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <OrdersPage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            
            <Route
              path="/team"
              element={
                user && user.role === "partner" ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <TeamPage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            
            <Route
              path="/bonuses"
              element={
                user && user.role === "partner" ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <BonusesPage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            
            <Route
              path="/messages"
              element={
                user ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <MessagesPage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/admin/products"
              element={
                user && hasAdminRank(user, ADMIN_RANKS.C) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <ProductsManagementPage />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            
            <Route
              path="/admin/activations"
              element={
                user && hasAdminRank(user, ADMIN_RANKS.B, ADMIN_RANKS.CAISSE) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <ActivationManagementPage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/admin/withdrawals"
              element={
                user && hasAdminRank(user, ADMIN_RANKS.B, ADMIN_RANKS.CAISSE) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <WithdrawalManagementPage />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/admin/kyc"
              element={
                user && hasAdminRank(user, ADMIN_RANKS.A) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <AdminKycPage />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/verification"
              element={
                user && user.role === "partner" ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <VerificationPage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/admin/founder"
              element={
                user && isFounderAccount(user) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <FounderDashboard />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/admin/artico-cash"
              element={
                user && canAccessCashPages(user) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <ArticoCashPage />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            
            <Route
              path="/admin/users"
              element={
                user && hasAdminRank(user, ADMIN_RANKS.GP, ADMIN_RANKS.CAISSE) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <UserManagementPage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/admin/users/all"
              element={
                user && hasFullAdminRank(user) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <AdminUsersDirectoryPage />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/admin/partners/:partnerId/lineage"
              element={
                user && hasAdminRank(user, ADMIN_RANKS.GP, ADMIN_RANKS.CAISSE) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <AdminPartnerLineagePage />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/admin/system"
              element={
                user && hasFullAdminRank(user) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <SystemOpsPage />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/admin/platform-settings"
              element={
                user && hasAdminRank(user, ADMIN_RANKS.GP) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <PlatformSettingsPage />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/admin/moderation"
              element={
                user && hasAdminRank(user, ADMIN_RANKS.GP) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <ModerationQueuePage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/analytics"
              element={
                user && (user.role !== "admin" || hasFullAdminRank(user) || canAccessCashPages(user)) ? (
                  <DashboardLayout user={user} onLogout={handleLogout}>
                    <AnalyticsDashboardPage user={user} />
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
          </Routes>
        </BrowserRouter>
      </CartProvider>
      <Toaster />
    </div>
  );
}

function AndroidBackButtonBridge({ user }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let listener;

    CapacitorApp.addListener("backButton", ({ canGoBack }) => {
      const path = window.location.pathname;

      if (path === "/dashboard") {
        CapacitorApp.exitApp();
        return;
      }

      if (canGoBack) {
        navigate(-1);
        return;
      }

      navigate(user ? "/dashboard" : "/");
    }).then((handle) => {
      listener = handle;
    });

    return () => {
      if (listener) listener.remove();
    };
  }, [navigate, location.pathname, user]);

  return null;
}

function needsEmailVerification(user) {
  return !!user && user.role !== "admin" && !user.email_verified;
}

function MobileSessionEntry({ user }) {
  if (user?.role === "admin") {
    return <Navigate to="/mobile/admin" replace />;
  }
  if (user?.role === "partner" || user?.role === "buyer") {
    return <Navigate to={`/mobile/${user.role}`} replace />;
  }
  return <MobileHomePage user={user} />;
}

export default App;
